/**
* Shared helpers for project detail and tab screens. Used by hooks and project screens.
*/
import dayjs from 'dayjs';
import { ENV } from '@configs/env';
import { formatLongDateTimeWithPipe } from '@utils/dateFormating.util';

/** Empty or invalid budget from a form → 0 for POST /projects */
export function budgetFromFormValue(v) {
    if (v === '' || v == null) return 0;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * GET /projects list query params (tenant workspace).
 * Sends `status`, `priority`, and `search` (project name) to the API; omit keys when not filtering.
 *
 * @param {{ statusTab: string, STATUS_TAB: { ALL: string, ACTIVE: string, INACTIVE: string }, filterPriority?: string, debouncedSearch?: string }} p
 * @returns {{ page: number, limit: number, status?: string, priority?: string, search?: string }}
 */
export function buildProjectsListQueryParams({ statusTab, STATUS_TAB, filterPriority, debouncedSearch }) {
    const params = { page: 1, limit: 100 };
    if (statusTab === STATUS_TAB.ACTIVE) params.status = 'active';
    else if (statusTab === STATUS_TAB.PENDING) params.status = 'pending_completion';
    else if (statusTab === STATUS_TAB.INACTIVE) params.status = 'inactive';
    else if (statusTab === STATUS_TAB.STARRED) params.isStarred = true;
    if (filterPriority) {
        const pr = String(filterPriority).toLowerCase().trim();
        if (pr) params.priority = pr;
    }
    const q = String(debouncedSearch ?? '').trim();
    if (q) params.q = q;
    return params;
}

export function truncateFileName(name, maxLen = 24) {
    if (!name || typeof name !== 'string' || name.length <= maxLen) return name;
    const extMatch = name.match(/\.[a-z0-9]+$/i);
    const ext = extMatch ? extMatch[0] : '';
    const base = ext ? name.slice(0, -ext.length) : name;
    const keep = maxLen - ext.length - 5;
    if (keep < 1) return name.slice(0, maxLen - 3) + '...';
    return base.slice(0, keep) + '.....' + ext;
}

/** Resolve relative attachment URLs for opening in new tab (same as Chat media URLs). */
export function resolveAttachmentUrl(url) {
    if (!url || typeof url !== 'string') return url;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    if (url.startsWith('mailto:') || url.startsWith('tel:') || url.startsWith('#')) return url;
    if (url.startsWith('//') && url.length > 2 && url[2] !== '/') {
        if (typeof window !== 'undefined') return `${window.location.protocol}${url}`;
        return `https:${url}`;
    }
    const path = url.startsWith('/') ? url : `/${url}`;
    try {
        const apiUrl = ENV.API_URL && ENV.API_URL.trim();
        const origin = apiUrl ? new URL(apiUrl).origin : window.location.origin;
        return `${origin}${path}`;
    } catch {
        return url;
    }
}

export const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
};

/** Discussion feed: YYYY-MM-DD HH:mm (matches project detail reference layout). */
export function formatDiscussionTimestamp(dateStr) {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return '—';
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
        return '—';
    }
}

/**
 * Project manager for display: API object, named user, or first assignee with manager-like role.
 */
export function resolveProjectManagerUser(project) {
    if (!project) return null;
    const raw = project.projectManager ?? project.manager ?? project.projectManagerUser ?? project.managerUser;
    if (raw && typeof raw === 'object' && (raw.id != null || raw.name || raw.email)) return raw;
    if (typeof raw === 'string' && raw.trim()) return { name: raw.trim() };
    const users = project.assignedUsers ?? [];
    const byRole = users.find((u) => {
        const r = (u.role?.name || u.roleName || '').toLowerCase();
        return r.includes('project manager') || r.includes('manager') || r === 'pm';
    });
    return byRole ?? null;
}

export const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');

/** Prefer API `milestoneNumber` (e.g. ML-1) for UI labels. */
export function getMilestoneDisplayLabel(milestone) {
    if (!milestone) return 'Milestone';
    const n = milestone.milestoneNumber ?? milestone.milestone_number ?? milestone.number;
    const numberLabel = typeof n === 'string' ? n.trim() : n != null ? String(n).trim() : '';
    if (numberLabel) return numberLabel;
    const name = milestone.name ?? milestone.title;
    const nameLabel = typeof name === 'string' ? name.trim() : name != null ? String(name).trim() : '';
    return nameLabel || 'Milestone';
}

/**
 * Group list rows by their milestone display label, keeping stable first-seen order.
 * @returns {Array<{ label: string, items: any[] }>}
 */
export function groupItemsByMilestoneLabel(items) {
    const list = Array.isArray(items) ? items : [];
    const order = [];
    const map = new Map();
    for (const it of list) {
        const label = getMilestoneDisplayLabel(it?.milestone);
        if (!map.has(label)) {
            map.set(label, []);
            order.push(label);
        }
        map.get(label).push(it);
    }
    return order.map((label) => ({ label, items: map.get(label) ?? [] }));
}

/** Prefer API `sprintNumber` (e.g. SP-1) for UI labels. */
export function getSprintDisplayLabel(sprint) {
    if (!sprint) return 'Sprint';
    const n = sprint.sprintNumber ?? sprint.sprint_number ?? sprint.number;
    const numberLabel = typeof n === 'string' ? n.trim() : n != null ? String(n).trim() : '';
    if (numberLabel) return numberLabel;
    const name = sprint.name ?? sprint.title;
    const nameLabel = typeof name === 'string' ? name.trim() : name != null ? String(name).trim() : '';
    return nameLabel || 'Sprint';
}

/** Format payment amount for display (e.g. 15000, "USD" -> "15,000 USD"). */
export const formatPaymentAmount = (amount, currency) => {
    if (amount == null || Number.isNaN(Number(amount))) return '—';
    const num = Number(amount);
    const formatted = num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const curr = (currency && String(currency).trim()) || '';
    return curr ? `${formatted} ${curr}` : formatted;
};

/** Aggregate payments into totals by currency and count. */
export const getPaymentSummary = (payments) => {
    const list = Array.isArray(payments) ? payments : [];
    const byCurrency = {};
    for (const p of list) {
        const amt = Number(p?.amount);
        if (!Number.isNaN(amt) && amt >= 0) {
            const c = (p?.currency && String(p.currency).trim()) || '—';
            byCurrency[c] = (byCurrency[c] || 0) + amt;
        }
    }
    return { byCurrency, installmentsCount: list.length };
};

/** Total received as display string (e.g. "15,000 USD · 1,200 EUR" or "—"). */
export const getPaymentTotalDisplay = (payments) => {
    const { byCurrency } = getPaymentSummary(payments);
    const entries = Object.entries(byCurrency).filter(([, v]) => v > 0);
    if (entries.length === 0) return '—';
    return entries.map(([curr, amt]) => formatPaymentAmount(amt, curr)).join(' · ');
};

/** Stable row key for payment table. */
export const getPaymentRowKey = (p) => p?.id ?? p?.createdAt ?? `${p?.amount}-${p?.currency}-${p?.createdAt ?? ''}`;

/** Installments label (e.g. "3 installments"). */
export const getInstallmentsLabel = (count) => (count === 1 ? '1 installment' : `${count} installments`);

/** Display label for task priority (e.g. "medium" -> "Medium"). */
export const formatPriorityLabel = (priority) => (priority ? capFirst(String(priority)) : null);

/** Display label for project type (e.g. "fixed_sdlc" -> "Fixed SDLC"). */
export const formatProjectType = (type) => {
    if (!type) return '—';
    const s = String(type).trim().toLowerCase();
    if (s === 'fixed_sdlc') return 'Fixed SDLC';
    if (s === 'retainer') return 'Retainer';
    if (s === 'time_and_material') return 'Time & Material';
    if (s === 'internal') return 'Internal';
    
    return String(type)
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((w) => {
            const upper = w.toUpperCase();
            if (['SDLC', 'QA', 'PM', 'IP', 'T&M'].includes(upper)) return upper;
            return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
        })
        .join(' ');
};


const titleCaseStatusWords = (str) =>
    String(str)
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');

/** Sprint task status for Kanban “Mark As Completed” column (replaces priority chip there). */
export const getSprintTaskStatusLabel = (task) => {
    const raw = task?.status;
    if (raw == null || raw === '') return '—';
    const s = String(raw).toLowerCase();
    if (s === 'done' || s === 'completed') return 'Completed';
    if (s === 'submitted_to_qa' || s === 'submitted_to_pm') return 'Submitted';
    if (s === 'todo') return 'To do';
    if (s === 'returned' || s === 'returned_by_qa' || s === 'qa_returned') return 'Returned';
    return titleCaseStatusWords(raw);
};

/** Task description markdown HTML from API (description.markdown). */
export const getTaskDescriptionMarkdown = (task) =>
    task?.description && typeof task.description.markdown === 'string' ? task.description.markdown.trim() : '';

/**
 * Sprint task id for API paths (`.../tasks/:taskId/complete`, PATCH body, etc.).
 * Prefer `taskId` (backend TaskId) over internal `id`.
 */
export const getSprintTaskApiId = (task) => {
    if (task == null) return undefined;
    const v = task.taskId ?? task.TaskId ?? task.id;
    return v != null && v !== '' ? String(v) : undefined;
};

/**
 * Sprint/kanban task creation time from API (camel/snake aliases; epoch seconds vs ms).
 * Safe to pass to dayjs / {@link formatCreatedAtSentenceStable} from `@utils/dateFormating.util`.
 */
export function getSprintTaskCreatedAtRaw(task) {
    if (task == null || typeof task !== 'object') return null;
    const pick =
        task.createdAt ??
        task.created_at ??
        task.CreatedAt ??
        task.createdOn ??
        task.created_on ??
        task.dateCreated ??
        task.date_created ??
        task.taskCreatedAt ??
        task.task_created_at ??
        null;
    if (pick == null || pick === '') return null;
    if (typeof pick === 'number' && Number.isFinite(pick)) {
        const n = Math.abs(pick);
        return n > 0 && n < 1e12 ? pick * 1000 : pick;
    }
    if (pick instanceof Date && !Number.isNaN(pick.getTime())) return pick.getTime();
    const s = String(pick).trim();
    return s.length > 0 ? s : null;
}

/** After TID-, keep only the first hyphen-separated segment (e.g. UUID → 9a5bd02b, not full 36 chars). */
const shortTaskIdSegmentAfterPrefix = (idPart) => {
    let t = String(idPart).trim();
    if (!t) return '';
    t = t.split(/\s+/)[0];
    const i = t.indexOf('-');
    if (i > 0) return t.slice(0, i);
    return t;
};

/** Display id for sprint task cards (e.g. TID-12 or TID-9a5bd02b for UUIDs). */
export const getTaskDisplayId = (task) => {
    const raw = task?.displayId ?? task?.taskCode ?? task?.code ?? task?.taskId ?? task?.id;
    if (raw == null || raw === '') return 'TID—';
    let s = String(raw).trim();
    if (!s) return 'TID—';
    s = s.replace(/^TID-\s*/i, '').trim();
    if (!s) return 'TID—';
    s = shortTaskIdSegmentAfterPrefix(s);
    if (!s) return 'TID—';
    return `TID-${s}`;
};

/**
 * Human-readable task ref for lists/cards (e.g. `TASK-12` from API when present, else `TID-…` via {@link getTaskDisplayId}).
 * Matches {@link SprintTaskCard} `taskNumberDisplay` precedence.
 */
export function getSprintTaskNumberLabel(task) {
    if (task == null || typeof task !== 'object') return '—';
    const n = task.taskNumber ?? task.task_number ?? task.number;
    if (n != null && String(n).trim() !== '') return String(n).trim();
    return getTaskDisplayId(task);
}

/** Count of file + link attachments on a task (best-effort from API shapes). */
export const getTaskAttachmentCount = (task) => {
    if (task == null) return 0;
    if (typeof task.attachmentCount === 'number' && task.attachmentCount >= 0) return task.attachmentCount;
    const files = task.files ?? task.fileAttachments;
    const links = task.attachments ?? task.linkAttachments;
    let n = 0;
    if (Array.isArray(files)) n += files.length;
    if (Array.isArray(links)) n += links.length;
    return n;
};

/**
 * Flat list of file + link attachments for read-only UI (e.g. sprint task modal).
 * @returns {Array<{ id: string, kind: 'file'|'link', name: string, rawUrl: string }>}
 */
export function getTaskAttachmentListForModal(task) {
    if (task == null) return [];
    const files = task.files ?? task.fileAttachments;
    const links = task.attachments ?? task.linkAttachments;
    const fromDesc =
        task?.description && typeof task.description === 'object' && Array.isArray(task.description.attachments)
            ? task.description.attachments
            : [];
    const out = [];
    let i = 0;
    for (const f of Array.isArray(files) ? files : []) {
        const raw = f?.url ?? f?.href ?? f?.fileUrl ?? f?.path ?? '';
        const name =
            f?.name ??
            f?.title ??
            f?.filename ??
            (raw ? truncateFileName(String(raw).split('/').pop() || 'File') : 'File');
        const id = f?.id ?? f?.fileId ?? `file-${i}`;
        i += 1;
        out.push({ id: String(id), kind: 'file', name, rawUrl: typeof raw === 'string' ? raw : '' });
    }
    for (const l of Array.isArray(links) ? links : []) {
        const raw = typeof l === 'string' ? l : l?.url ?? l?.href ?? '';
        if (!raw) continue;
        const name = typeof l === 'object' && l != null ? l.title ?? l.name ?? raw : raw;
        out.push({ id: `link-${i}`, kind: 'link', name: String(name || raw), rawUrl: raw });
        i += 1;
    }
    // Some APIs (e.g. QA submissions) emit task attachments inside `description.attachments`.
    for (const a of Array.isArray(fromDesc) ? fromDesc : []) {
        if (!a || typeof a !== 'object') continue;
        const raw = typeof a.url === 'string' ? a.url : '';
        if (!raw) continue;
        const kind = String(a.type || '').toLowerCase() === 'link' ? 'link' : 'file';
        const name = a.title ?? a.name ?? (raw ? truncateFileName(String(raw).split('/').pop() || 'File') : 'File');
        out.push({ id: `${kind}-${i}`, kind, name: String(name || raw), rawUrl: raw });
        i += 1;
    }
    return out;
}

/** Markdown string for bug description (API may return string or { markdown }). */
export const getBugDescriptionMarkdown = (bug) => {
    const d = bug?.description;
    if (d == null) return '';
    if (typeof d === 'string') return d.trim();
    if (typeof d?.markdown === 'string') return d.markdown.trim();
    if (typeof d?.content === 'string') return d.content.trim();
    return '';
};

/** Task was sent back from QA (returned / returned_by_qa / qa flags). */
export const isTaskReturnedByQa = (task) => {
    const s = String(task?.status ?? '').toLowerCase();
    if (s === 'returned' || s === 'returned_by_qa' || s === 'qa_returned') return true;
    const qa = task?.qaStatus ?? task?.qa_status;
    if (qa != null && String(qa).toLowerCase().includes('return')) return true;
    return false;
};

/** Client declined / task is on tenant dev backlog (`isBacklog` / `is_backlog`). */
export const isTaskOnDevBacklog = (task) => {
    if (!task || typeof task !== 'object') return false;
    return Boolean(task.isBacklog ?? task.is_backlog);
};

/** PMO / backlog flow: submitted as change request (`isChangeRequest` / `is_change_request`). */
export const isTaskMarkedChangeRequest = (task) => {
    if (!task || typeof task !== 'object') return false;
    return Boolean(task.isChangeRequest ?? task.is_change_request);
};

/** Task spawned from Issues & upgrades (`isIssueOrUpgrade` / `is_issue_or_upgrade`, or task type). */
export const isTaskMarkedIssueOrUpgrade = (task) => {
    if (!task || typeof task !== 'object') return false;
    if (Boolean(task.isIssueOrUpgrade ?? task.is_issue_or_upgrade)) return true;
    const t = String(task?.type ?? task?.taskType ?? task?.task_type ?? '').toLowerCase();
    return t === 'issue' || t === 'issue_upgrade' || t === 'issueupgrade' || t === 'issues_and_upgrades';
};

/** Client-facing ticket origin (`isClientTicket` / `is_client_ticket`). */
export const isTaskMarkedClientTicket = (task) => {
    if (!task || typeof task !== 'object') return false;
    return Boolean(task.isClientTicket ?? task.is_client_ticket);
};

/** Task tied to blocker workflow (`isBlocker` / `is_blocker`). */
export const isTaskMarkedBlocker = (task) => {
    if (!task || typeof task !== 'object') return false;
    return Boolean(task.isBlocker ?? task.is_blocker);
};

/**
 * Kanban task-card footer badges: QA/bugs first, then blocker, backlog, PMO/origin flags.
 * @param {object} task
 * @param {{ bugsExternalCount?: number }} [options] — parent may pass richer `bugs` list than `task.bugs`.
 * @returns {Array<{ key: string, tooltip: string }>}
 */
export const getTaskKanbanFootprintIndicators = (task, options = {}) => {
    if (!task || typeof task !== 'object') return [];

    const external = typeof options.bugsExternalCount === 'number' ? options.bugsExternalCount : 0;
    const taskBugsLen = Array.isArray(task.bugs) ? task.bugs.length : 0;
    const bugsCountEffective = Math.max(external, taskBugsLen);
    const qaReturn = isTaskReturnedByQa(task);

    /** @type {Array<{ key: string, tooltip: string }>} */
    const items = [];

    if (bugsCountEffective > 0 || qaReturn) {
        const tooltip =
            bugsCountEffective > 0
                ? `${bugsCountEffective} bug${bugsCountEffective === 1 ? '' : 's'} reported`
                : 'Returned from QA';
        items.push({ key: 'bug', tooltip });
    }
    if (isTaskMarkedBlocker(task)) {
        items.push({ key: 'blocker', tooltip: 'Blocker task' });
    }
    if (isTaskOnDevBacklog(task)) {
        items.push({ key: 'backlog', tooltip: 'Dev backlog task' });
    }
    if (isTaskMarkedChangeRequest(task)) {
        items.push({ key: 'changeRequest', tooltip: 'Change request' });
    }
    if (isTaskMarkedIssueOrUpgrade(task)) {
        items.push({ key: 'issueOrUpgrade', tooltip: 'Issue or upgrade' });
    }
    if (isTaskMarkedClientTicket(task)) {
        items.push({ key: 'clientTicket', tooltip: 'Client ticket' });
    }

    return items;
};

/**
 * QA Kanban Todo column: only tasks that are in the QA submission flow (submitted to QA, or returned by QA),
 * not plain sprint todo/completed work that has not been submitted to QA yet.
 */
export const isTaskEligibleForQaTodoColumn = (task) => {
    const s = String(task?.status ?? '').toLowerCase();
    if (s === 'submitted_to_qa') return true;
    return isTaskReturnedByQa(task);
};

/** Plain text heading for a bug card (description or steps, stripped of HTML). */
export const getBugHeading = (bug, maxLen = 100) => {
    const str = bug?.description ?? bug?.stepsToReproduce ?? '';
    const s = typeof str === 'string' ? str.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : '';
    if (!s) return 'Bug';
    return s.length <= maxLen ? s : `${s.slice(0, maxLen)}…`;
};

/** Blocker description text from API (string or { markdown, content }). */
export const getBlockerDescriptionText = (blocker) => {
    const d = blocker?.description;
    if (d == null) return '—';
    if (typeof d === 'string') return d.trim() || '—';
    if (typeof d?.markdown === 'string') return d.markdown.trim() || '—';
    if (typeof d?.content === 'string') return d.content.trim() || '—';
    return '—';
};

/** Short display label for blocker in mentions: title or truncated description. */
export const getBlockerDisplayName = (blocker, maxLen = 48) => {
    const title = blocker?.title ?? blocker?.name;
    if (title && String(title).trim()) return String(title).trim();
    const desc = getBlockerDescriptionText(blocker);
    if (!desc || desc === '—') return 'Blocker';
    const s = String(desc).replace(/\s+/g, ' ').trim();
    return s.length <= maxLen ? s : `${s.slice(0, maxLen)}…`;
};

/** Blocker description HTML/markdown for rich display (same shape as task: description.markdown or string). */
export const getBlockerDescriptionMarkdown = (blocker) => {
    const d = blocker?.description;
    if (d == null) return '';
    if (typeof d === 'string') return d.trim();
    if (typeof d?.markdown === 'string') return d.markdown.trim();
    if (typeof d?.content === 'string') return d.content.trim();
    return '';
};

/** Blocker id for API paths (PATCH …/blockers/:blockerId). */
export const getBlockerApiId = (blocker) => {
    if (blocker == null) return undefined;
    const v = blocker.blockerId ?? blocker.id;
    return v != null && v !== '' ? String(v) : undefined;
};

/** Display id for blocker cards (e.g. BID-12). */
export const getBlockerDisplayId = (blocker) => {
    const raw = blocker?.displayId ?? blocker?.blockerCode ?? blocker?.code ?? blocker?.id;
    if (raw == null || raw === '') return 'BID—';
    let s = String(raw).trim();
    if (!s) return 'BID—';
    s = s.replace(/^BID-\s*/i, '').trim();
    if (!s) return 'BID—';
    s = shortTaskIdSegmentAfterPrefix(s);
    if (!s) return 'BID—';
    return `BID-${s}`;
};

/** Count of file + link attachments on a blocker (same heuristics as tasks). */
export const getBlockerAttachmentCount = (blocker) => {
    if (blocker == null) return 0;
    if (typeof blocker.attachmentCount === 'number' && blocker.attachmentCount >= 0) return blocker.attachmentCount;
    const files = blocker.files ?? blocker.fileAttachments;
    const links = blocker.attachments ?? blocker.linkAttachments;
    let n = 0;
    if (Array.isArray(files)) n += files.length;
    if (Array.isArray(links)) n += links.length;
    return n;
};

/** Whether a blocker counts as resolved for Active vs Resolved columns. */
export const isBlockerResolved = (blocker) => {
    if (blocker?.resolved === true) return true;
    const s = String(blocker?.status ?? '').toLowerCase();
    if (['resolved', 'completed', 'closed', 'done'].includes(s)) return true;
    return false;
};

/** API body to set blocker resolved state (drag Active ↔ Resolved). */
export const blockerResolvedPatchBody = (resolved) =>
    resolved ? { status: 'resolved' } : { status: 'open' };

/** Split blockers into active and resolved lists. */
export const partitionBlockersByStatus = (blockers) => {
    const list = Array.isArray(blockers) ? blockers : [];
    const active = [];
    const resolved = [];
    for (const b of list) {
        if (isBlockerResolved(b)) resolved.push(b);
        else active.push(b);
    }
    return { activeBlockers: active, resolvedBlockers: resolved };
};

/** Discussion post content HTML for rich display (content string or content.markdown from API). */
export const getDiscussionContentHtml = (post) => {
    const c = post?.content;
    if (c == null) return '';
    if (typeof c === 'string') return c.trim();
    if (typeof c?.markdown === 'string') return c.markdown.trim();
    if (typeof c?.content === 'string') return c.content.trim();
    return '';
};

/** Feedback item content HTML for rich display (content string or content.markdown from API). */
export const getFeedbackContentHtml = (item) => {
    const c = item?.content;
    if (c == null) return '';
    if (typeof c === 'string') return c.trim();
    if (typeof c?.markdown === 'string') return c.markdown.trim();
    if (typeof c?.content === 'string') return c.content.trim();
    return '';
};

/** True when HTML content has no meaningful text (only tags/line breaks/whitespace). Use before submitting discussion post. */
export const isDiscussionContentEmpty = (html) => {
    if (html == null || typeof html !== 'string') return true;
    const text = html
        .replace(/<[^>]+>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    return text.length === 0;
};

/** Plain text preview from HTML/markdown string (strip tags, optional truncate). */
export const stripHtmlPreview = (html, maxLength = 120) => {
    if (html == null || typeof html !== 'string') return '';
    const text = html
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (maxLength && text.length > maxLength) return text.slice(0, maxLength).trim() + '…';
    return text;
};

/** Stable author user id for discussion post (for alignment / avatar). */
export const getDiscussionAuthorId = (post) => {
    if (!post) return null;
    return (
        post.createdBy ??
        post.createdByUser?.id ??
        post.author?.id ??
        post.user?.id ??
        post.userId ??
        null
    );
};

/** Display name for discussion post: createdByUser.name, createdByName, author.name, or fallback. */
export const getDiscussionAuthorName = (post) => {
    const name =
        post?.createdByUser?.name ??
        post?.createdByName ??
        post?.author?.name ??
        post?.user?.name;
    if (name && typeof name === 'string') return name;
    const author = post?.author ?? post?.user;
    if (author && typeof author === 'string') return author;
    return 'Unknown';
};

/** Role for discussion post: createdByUser.roles[0] or role name. */
export const getDiscussionAuthorRole = (post) => {
    const roles = post?.createdByUser?.roles;
    if (Array.isArray(roles) && roles.length > 0) {
        const r = roles[0];
        return typeof r === 'string' ? r : r?.name ?? '';
    }
    return post?.createdByUser?.role ?? post?.author?.role ?? post?.user?.role ?? '';
};

/** Avatar initials for discussion post: from name (createdByUser.name, createdByName, etc.) or first 2 chars of id. */
export const getDiscussionAuthorInitials = (post) => {
    const name =
        post?.createdByUser?.name ??
        post?.createdByName ??
        post?.author?.name ??
        post?.user?.name ??
        post?.author ??
        post?.user;
    if (name && typeof name === 'string') return getInitials(name);
    const id = post?.createdBy ?? post?.id;
    if (id && typeof id === 'string') return id.slice(0, 2).toUpperCase();
    return '?';
};

/** Short date-time for discussion (e.g. 2025-06-30 01:32). */
export const formatDiscussionDateTime = (dateStr) => {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).replace(/\//g, '-');
    } catch {
        return String(dateStr);
    }
};

/** Activity log: "September 22, 2026 03:10PM" (no space before AM/PM). */
export const formatActivityLogDateTime = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return '—';
        const s = d.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
        // "September 22, 2026 at 03:10 PM" (some locales) → normalize
        return s.replace(/\s+at\s+/i, ' ').replace(/\s?(AM|PM)$/i, '$1');
    } catch {
        return String(dateStr);
    }
};

export const getInitials = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (name[0] || '?').toUpperCase();
};

/** Stable avatar background color from id (or string). Returns hex. */
const AVATAR_COLORS = ['#5B8DEE', '#34C3B5', '#E85D75', '#F5A623', '#9B59B6', '#1ABC9C', '#E74C3C', '#3498DB', '#F39C12', '#2ECC71', '#E91E63', '#00BCD4'];
export const getAvatarColor = (id) => {
    if (id == null) return AVATAR_COLORS[0];
    const str = String(id);
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = ((hash << 5) - hash) + str.charCodeAt(i) | 0;
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

function taskCardUserRefFrom(obj) {
    if (!obj || typeof obj !== 'object') return null;
    const name = String(obj.name ?? obj.fullName ?? obj.displayName ?? obj.email ?? '').trim();
    const id = obj.id ?? obj._id ?? null;
    if (!name) return null;
    return { id, name };
}

/** Creator only (`createdBy` / `created_by`). */
export function getTaskCardCreatorPersonStrict(task) {
    if (!task || typeof task !== 'object') return null;
    return taskCardUserRefFrom(task.createdBy ?? task.created_by);
}

export function getTaskCardAssigneePerson(task) {
    if (!task || typeof task !== 'object') return null;
    return taskCardUserRefFrom(task.assigneeId ?? task.assignee_id ?? task.assignee ?? task.assignedTo);
}

/**
 * Sprint/kanban task card: person metadata (prefers creator; falls back to assignee).
 * @returns {{ id: *, name: string, source: 'creator'|'assignee' } | null}
 */
export function getTaskCardCreatedByPerson(task) {
    const fromCreator = getTaskCardCreatorPersonStrict(task);
    if (fromCreator) return { ...fromCreator, source: 'creator' };
    const fromAssignee = getTaskCardAssigneePerson(task);
    if (fromAssignee) return { ...fromAssignee, source: 'assignee' };
    return null;
}

/** Display name for user/contributor: name or email or fallback. */
export const getDisplayName = (record) => record?.name || record?.email || '—';

export const projectsListPath = (pathname) => (pathname?.startsWith('/tenant') ? '/tenant/projects' : '/projects');

export const projectChatPath = (pathname, chatId) =>
    pathname?.startsWith('/tenant') ? `/tenant/chat/${chatId}` : `/chat/${chatId}`;

export const milestoneStatusAccent = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'completed' || s === 'done') return '#52c41a';
    if (s === 'in_progress' || s === 'in progress') return '#1890ff';
    if (s === 'pending') return '#faad14';
    if (s === 'delayed') return '#ff7875';
    return undefined;
};

/** Sprint description can be string or object { content: string } from API */
export const getSprintDescriptionText = (sprint) => {
    const d = sprint?.description;
    if (d == null) return '';
    if (typeof d === 'string') return d.trim();
    if (typeof d === 'object' && typeof d.content === 'string') return d.content.trim();
    return '';
};

/** Milestone description from API: string, `{ markdown }`, `{ summary, goals }`, etc. */
export const getMilestoneDescriptionText = (milestone) => {
    const d = milestone?.description;
    if (d == null) return '';
    if (typeof d === 'string') return d.trim();
    if (typeof d === 'object' && typeof d.markdown === 'string') return d.markdown.trim();
    if (typeof d === 'object' && typeof d.summary === 'string') return d.summary.trim();
    if (Array.isArray(d.goals) && d.goals.length > 0) return `<ul>${d.goals.map((g) => `<li>${String(g)}</li>`).join('')}</ul>`;
    return '';
};

/** Raw summary HTML (unsanitized) from milestone `description` for attachment edits. */
export function getMilestoneDescriptionSummaryHtml(milestone) {
    const d = milestone?.description;
    if (d == null) return '';
    if (typeof d === 'string') return d;
    if (typeof d === 'object' && typeof d.summary === 'string') return d.summary;
    return '';
}

/** Sprint deadline row: "Month d, yyyy  →  Month d, yyyy" (aligned with StatusBadge wording). */
export function formatSprintDeadlineRange(sprint, formatDateFn) {
    const fmt = typeof formatDateFn === 'function' ? formatDateFn : formatDate;
    const a = fmt(sprint?.startDate) ?? '—';
    const b = fmt(sprint?.endDate) ?? '—';
    return `${a}  →  ${b}`;
}

/** Human-readable sprint status (aligned with `StatusBadge`). */
export function formatSprintStatusLabel(status) {
    if (!status) return '—';
    const s = String(status).trim();
    const lower = s.toLowerCase();
    if (lower === 'in_progress') return 'In Progress';
    if (lower === 'submitted_to_qa' || lower === 'submitted_to_pm') return 'Submitted';
    if (lower === 'returned_by_qa') return 'Returned by QA';
    if (lower === 'pending_completion') return 'Pending completion';
    if (s.includes('_')) {
        return s
            .split('_')
            .map((word) => (word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''))
            .join(' ');
    }
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** Stable key for merging GET /projects/:id/bugs onto sprint tasks (matches QA submission task shape). */
function bugTargetTaskIdKey(bug) {
    const raw =
        getSprintTaskApiId(bug?.task) ?? bug?.taskId ?? bug?.task?.id ?? bug?.task?.taskId ?? bug?.task?.sprintTaskId;
    if (raw == null || raw === '') return null;
    const key = String(raw).trim();
    return key || null;
}

function sprintTaskIdMatchKey(task) {
    const raw =
        getSprintTaskApiId(task) ?? task?.taskId ?? task?.id ?? task?.sprintTaskId ?? task?.sprint_task_id;
    if (raw == null || raw === '') return null;
    const key = String(raw).trim();
    return key || null;
}

/**
 * Attach bugs from GET /projects/:id/bugs onto matching sprint tasks (by task id).
 * Uses the same id precedence as {@link getSprintTaskApiId} so QA boards match API `taskId` / nested `task`.
 * @param {Array} sprints
 * @param {Array} bugs
 */
export function mergeBugsIntoSprintTasks(sprints, bugs) {
    if (!Array.isArray(sprints) || sprints.length === 0) return Array.isArray(sprints) ? sprints : [];
    const byTask = new Map();
    for (const b of Array.isArray(bugs) ? bugs : []) {
        const key = bugTargetTaskIdKey(b);
        if (!key) continue;
        if (!byTask.has(key)) byTask.set(key, []);
        byTask.get(key).push(b);
    }
    return sprints.map((s) => ({
        ...s,
        tasks: (Array.isArray(s.tasks) ? s.tasks : []).map((t) => {
            const matchKey = sprintTaskIdMatchKey(t);
            const list = matchKey ? byTask.get(matchKey) : undefined;
            const fromApi = Array.isArray(list) ? list : [];
            const existing = Array.isArray(t.bugs) ? t.bugs : [];
            if (fromApi.length === 0) return { ...t, bugs: existing };
            const seen = new Set();
            const merged = [];
            for (const bug of [...fromApi, ...existing]) {
                const bid = bug?.id ?? bug?.bugId;
                const sid = bid != null ? String(bid) : JSON.stringify(bug);
                if (seen.has(sid)) continue;
                seen.add(sid);
                merged.push(bug);
            }
            return { ...t, bugs: merged };
        }),
    }));
}

/**
 * QA Kanban: Todo (not submitted to PMO — tasks in QA queue / returned by QA) | tasks with bugs | submitted_to_pm.
 * Tasks with status `submitted_to_qa` stay in Todo even if bug records remain on the payload (e.g. after return + resubmit).
 */
/** Total tasks on a sprint shown on the QA board (all columns). */
export function countSprintQaTasks(sprint) {
    const tasks = Array.isArray(sprint?.tasks) ? sprint.tasks : [];
    return tasks.length;
}

/** True if this milestone has at least one sprint with at least one task (QA tab can show work). */
export function milestoneHasSprintsWithTasks(milestoneId, sprints) {
    if (milestoneId == null) return false;
    const list = Array.isArray(sprints) ? sprints : [];
    return list.some(
        (s) =>
            String(s.milestoneId ?? s.milestone?.id ?? '') === String(milestoneId) &&
            countSprintQaTasks(s) > 0,
    );
}

export function partitionQaSprintTasks(tasks) {
    const list = Array.isArray(tasks) ? tasks : [];
    const todos = [];
    const bugsReported = [];
    const resolved = [];
    const submittedToPmo = [];
    for (const t of list) {
        const s = String(t?.status ?? '').toLowerCase();
        if (s === 'resolved') {
            resolved.push(t);
            continue;
        }
        const bugList = t.bugs ?? [];
        const hasOpenBugs = Array.isArray(bugList) && bugList.some((b) => String(b?.status ?? '').toLowerCase() === 'open');
        if (hasOpenBugs) {
            bugsReported.push(t);
            continue;
        }
        if (s === 'submitted_to_pm') {
            submittedToPmo.push(t);
            continue;
        }
        if (s === 'submitted_to_qa') {
            todos.push(t);
            continue;
        }
        if (isTaskEligibleForQaTodoColumn(t)) {
            todos.push(t);
        }
    }
    return { todos, bugsReported, resolved, submittedToPmo };
}

/**
 * Stable ordering for QA Kanban columns: title/name, then sprint task id.
 * @param {Array} tasks
 */
export function sortQaKanbanTasks(tasks) {
    const list = Array.isArray(tasks) ? tasks : [];
    return [...list].sort((a, b) => {
        const titleA = String(a?.title ?? a?.name ?? '').toLowerCase();
        const titleB = String(b?.title ?? b?.name ?? '').toLowerCase();
        if (titleA !== titleB) return titleA.localeCompare(titleB);
        const idA = getSprintTaskApiId(a) ?? '';
        const idB = getSprintTaskApiId(b) ?? '';
        return String(idA).localeCompare(String(idB));
    });
}

/** Same normalization as `partitionSprintTasks` in `useProjectSprints.js` (done → completed). */
function normalizeSprintTaskStatusForKanban(status) {
    const s = String(status ?? '').toLowerCase();
    if (s === 'done') return 'completed';
    return s;
}

function nonNegativeProgressCount(value) {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * Sprint tab / board progress: % = (completed + submitted) / total tasks.
 * Submitted = `submitted_to_qa` | `submitted_to_pm` (same columns as tenant sprint Kanban).
 */
export function getSprintProgressPercentFromTasks(tasks) {
    const list = Array.isArray(tasks) ? tasks : [];
    if (list.length === 0) return null;
    let todo = 0;
    let completed = 0;
    let submitted = 0;
    for (const t of list) {
        const s = normalizeSprintTaskStatusForKanban(t?.status);
        if (s === 'submitted_to_qa' || s === 'submitted_to_pm') submitted += 1;
        else if (s === 'completed') completed += 1;
        else todo += 1;
    }
    const total = todo + completed + submitted;
    if (total <= 0) return 0;
    const done = completed + submitted;
    return Math.round((done / total) * 100);
}

export const getSprintProgressPercent = (sprint) => {
    const tasks = Array.isArray(sprint?.tasks) ? sprint.tasks : [];
    const fromTasks = getSprintProgressPercentFromTasks(tasks);
    if (fromTasks != null) return fromTasks;

    if (String(sprint?.status).toLowerCase() === 'submitted_to_qa') return 100;

    const p = sprint?.progress;
    if (p && typeof p === 'object') {
        const todoCount = nonNegativeProgressCount(
            p.todoTasks ?? p.todoTasksCount ?? p.todo ?? p.todoCount,
        );
        const completedCount = nonNegativeProgressCount(
            p.completedTasks ?? p.completedTasksCount ?? p.completed ?? p.completedCount,
        );
        const submittedCount = nonNegativeProgressCount(
            p.submittedTasks ?? p.submittedTasksCount ?? p.submitted ?? p.submittedCount,
        );
        const derivedTotal = todoCount + completedCount + submittedCount;
        const reportedTotal = nonNegativeProgressCount(p.totalTasks ?? p.total ?? p.taskCount);
        const totalTasks = reportedTotal > 0 ? reportedTotal : derivedTotal;
        if (totalTasks <= 0) return 0;
        const done = Math.min(completedCount + submittedCount, totalTasks);
        return Math.round((done / totalTasks) * 100);
    }

    const num = typeof sprint?.progressPercent === 'number' ? sprint.progressPercent : sprint?.progress;
    return typeof num === 'number' ? Math.round(num) : 0;
};

/** Synthetic id for milestones with no `phaseId` when the project has phases. */
export const MILESTONE_UNASSIGNED_PHASE_KEY = '__unassigned__';

/** All sprint tasks under a milestone (flattened). */
export function flattenMilestoneTasks(milestone) {
    const sprints = milestone?.sprints ?? [];
    const out = [];
    for (const sp of sprints) {
        for (const t of sp.tasks ?? []) {
            out.push({ ...t, _sprintId: sp.id, _sprintName: sp.name });
        }
    }
    return out;
}

export function isMilestoneTaskCompleted(status) {
    const s = String(status || '').toLowerCase();
    return s === 'completed' || s === 'done';
}

/** Deliverable progress: % of tasks marked completed/done. */
export function getMilestoneTaskCompletionPercent(milestone) {
    const tasks = flattenMilestoneTasks(milestone);
    if (tasks.length === 0) return 0;
    const done = tasks.filter((t) => isMilestoneTaskCompleted(t.status)).length;
    return Math.round((done / tasks.length) * 100);
}

/**
 * Milestone deliverables from GET /projects/:id (`milestone.deliverables`).
 * @returns {Array<{ id: string, value: string, isMarked: boolean }>}
 */
export function getMilestoneDeliverablesList(milestone) {
    const raw = milestone?.deliverables;
    if (!Array.isArray(raw)) return [];
    return raw.map((d) => ({
        id: d.id,
        value: typeof d.value === 'string' ? d.value : String(d.name ?? ''),
        isMarked: Boolean(d.isMarked ?? d.is_marked),
    }));
}

/** Completion % from API deliverables (`isMarked`). */
export function getMilestoneDeliverableCompletionPercent(milestone) {
    const list = getMilestoneDeliverablesList(milestone);
    if (list.length === 0) return 0;
    const done = list.filter((d) => d.isMarked).length;
    return Math.round((done / list.length) * 100);
}

/** Display name for a phase (API uses `title`; legacy may use `name`). */
export function getPhaseDisplayTitle(phase) {
    if (!phase) return '';
    return (phase.phaseNumber ?? phase.title ?? '').trim() || 'Phase';
}

function firstNonEmptyString(...values) {
    for (const value of values) {
        const text = typeof value === 'string' ? value.trim() : value != null ? String(value).trim() : '';
        if (text) return text;
    }
    return '';
}

/** Fixed-width chip parts: number badge + title (ellipsis). */
export function getScopeChipParts(item, kind) {
    if (kind === 'phase') {
        return {
            number: firstNonEmptyString(item?.phaseNumber, item?.phase_number, 'PH'),
            title: firstNonEmptyString(item?.title, item?.name, 'Untitled phase'),
        };
    }
    if (kind === 'milestone') {
        return {
            number: firstNonEmptyString(item?.milestoneNumber, item?.milestone_number, item?.number, 'ML'),
            title: firstNonEmptyString(item?.name, item?.title, 'Untitled milestone'),
        };
    }
    return {
        number: firstNonEmptyString(item?.sprintNumber, item?.sprint_number, item?.number, 'SP'),
        title: firstNonEmptyString(item?.name, item?.title, 'Untitled sprint'),
    };
}

/** Phase description from API (string, or { markdown | content | summary }). */
export function getPhaseDescriptionText(phase) {
    const d = phase?.description;
    if (d == null) return '';
    if (typeof d === 'string') return d.trim();
    if (typeof d === 'object' && typeof d.markdown === 'string') return d.markdown.trim();
    if (typeof d === 'object' && typeof d.content === 'string') return d.content.trim();
    if (typeof d === 'object' && typeof d.summary === 'string') return d.summary.trim();
    return '';
}

/** Normalize bare / www URLs for opening in a new tab. */
export function normalizePhaseLinkUrl(url) {
    const u = String(url ?? '').trim();
    if (!u) return '';
    if (/^https?:\/\//i.test(u) || u.startsWith('mailto:') || u.startsWith('tel:')) return u;
    if (u.startsWith('//') && u.length > 2) return `https:${u}`;
    return `https://${u.replace(/^\/+/, '')}`;
}

/**
 * Convert plain `http(s)://...` and `www....` literals into markdown links.
 * Intended for display-only rendering when backend returns plain text.
 *
 * - Keeps existing markdown links unchanged.
 * - Skips conversion when the string looks like HTML (we expect editor HTML already contains <a> tags).
 */
export function linkifyPlainUrlsForMarkdown(input) {
    const s = typeof input === 'string' ? input : String(input ?? '');
    if (!s.trim()) return '';
    if (/<[^>]+>/.test(s)) return s;

    // Avoid touching already-linked markdown: [label](url)
    const protectedRanges = [];
    const mdLinkRe = /\[[^\]]+\]\([^)]+\)/g;
    let m;
    while ((m = mdLinkRe.exec(s))) {
        protectedRanges.push([m.index, m.index + m[0].length]);
    }
    const isProtected = (idx) => protectedRanges.some(([a, b]) => idx >= a && idx < b);

    const urlRe = /\b(https?:\/\/[^\s<]+|www\.[^\s<]+)\b/gi;
    let out = '';
    let last = 0;
    while ((m = urlRe.exec(s))) {
        const start = m.index;
        if (isProtected(start)) continue;
        const rawUrl = m[0];
        const href = normalizePhaseLinkUrl(rawUrl);
        out += s.slice(last, start);
        out += `[${rawUrl}](${href})`;
        last = start + rawUrl.length;
    }
    out += s.slice(last);
    return out;
}

/**
 * Convert plain URL literals inside HTML text nodes into `<a href="...">...</a>`.
 * - Skips existing anchors and attachment/mention pseudo-links.
 * - Uses `normalizePhaseLinkUrl` for `www.` and bare domains.
 *
 * Intended for display-only: when backend/editor stored HTML but left plain URLs unlinked.
 */
export function linkifyPlainUrlsInHtml(html) {
    const s = typeof html === 'string' ? html : String(html ?? '');
    if (!s.trim()) return '';
    if (typeof document === 'undefined') return s;
    if (!/<[^>]+>/.test(s)) return s;

    try {
        const doc = document.implementation.createHTMLDocument('');
        const root = doc.createElement('div');
        root.innerHTML = s;

        const urlRe = /\b(https?:\/\/[^\s<]+|www\.[^\s<]+)\b/gi;

        const isInsideAnchor = (node) => {
            let cur = node?.parentNode;
            while (cur && cur !== root) {
                if (cur.nodeType === Node.ELEMENT_NODE && cur.tagName === 'A') return true;
                cur = cur.parentNode;
            }
            return false;
        };

        const walk = (node) => {
            if (!node) return;
            if (node.nodeType === Node.TEXT_NODE) {
                if (isInsideAnchor(node)) return;
                const text = node.nodeValue || '';
                if (!text.trim()) return;
                if (!urlRe.test(text)) {
                    urlRe.lastIndex = 0;
                    return;
                }
                urlRe.lastIndex = 0;

                const frag = doc.createDocumentFragment();
                let last = 0;
                let m;
                while ((m = urlRe.exec(text))) {
                    const start = m.index;
                    const end = start + m[0].length;
                    const before = text.slice(last, start);
                    if (before) frag.appendChild(doc.createTextNode(before));

                    const rawUrl = m[0];
                    const a = doc.createElement('a');
                    a.setAttribute('href', normalizePhaseLinkUrl(rawUrl));
                    a.setAttribute('target', '_blank');
                    a.setAttribute('rel', 'noopener noreferrer');
                    a.textContent = rawUrl;
                    frag.appendChild(a);

                    last = end;
                }
                const after = text.slice(last);
                if (after) frag.appendChild(doc.createTextNode(after));

                node.parentNode?.replaceChild(frag, node);
                return;
            }

            if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node;
                // Don't descend into anchors (already linked), and don't touch scripts/styles.
                const tag = el.tagName?.toLowerCase();
                if (tag === 'a' || tag === 'script' || tag === 'style') return;
            }

            const children = node.childNodes ? Array.from(node.childNodes) : [];
            children.forEach(walk);
        };

        walk(root);
        return root.innerHTML;
    } catch {
        return s;
    }
}

/** File extension from URL path (uppercase, no dot), e.g. `.pdf` → `PDF`. */
export function getAttachmentFileExtension(url) {
    if (!url || typeof url !== 'string') return '';
    const pathOnly = url.split(/[?#]/)[0];
    const match = pathOnly.match(/\.([a-z0-9]{1,8})$/i);
    return match ? match[1].toUpperCase() : '';
}

/** Whether the URL is probably an image (extension or `data:image/`). */
export function isLikelyImageAttachmentUrl(url) {
    if (!url || typeof url !== 'string') return false;
    if (url.startsWith('data:image/')) return true;
    const ext = getAttachmentFileExtension(url).toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico', 'avif', 'heic'].includes(ext);
}

const NON_IMAGE_ATTACHMENT_EXTENSIONS = new Set([
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'txt',
    'csv',
    'zip',
    'rar',
    '7z',
    'tar',
    'gz',
    'mp4',
    'mov',
    'avi',
    'mkv',
    'mp3',
    'wav',
    'ogg',
    'json',
    'xml',
]);

/**
 * Classify URLs that should render as file/image cards in attachments UI,
 * even if backend emits them from a "links" field.
 */
export function isLikelyFileAttachmentUrl(url) {
    if (!url || typeof url !== 'string') return false;
    if (isLikelyImageAttachmentUrl(url)) return true;
    const value = String(url).trim();
    if (!value) return false;

    let pathOnly = value.split(/[?#]/)[0];
    try {
        const parsed = new URL(normalizePhaseLinkUrl(value));
        pathOnly = parsed.pathname || pathOnly;
    } catch {
        // keep fallback path parsing for relative URLs
    }

    const fileName = pathOnly.split('/').pop() || '';
    const match = fileName.match(/\.([a-z0-9]{1,8})$/i);
    if (!match) return false;
    const ext = match[1].toLowerCase();
    return NON_IMAGE_ATTACHMENT_EXTENSIONS.has(ext);
}

/**
 * Normalize QA / PMO submission attachment payloads (`attachments`, `links`, `files`, `notes.attachments`).
 * Dedupes by `type|url`; infers link vs file from URL when needed.
 *
 * @returns {Array<{ type: 'link'|'file', url: string, title: string }>}
 */
/** Canonical id on a QA/PMO submission record (matches {@link useProjectQA} lookups). */
export function extractQaSubmissionRecordId(sub) {
    if (!sub || typeof sub !== 'object') return null;
    return sub.id ?? sub._id ?? sub.submissionId ?? sub.sid ?? sub.submission_id ?? null;
}

function qaSubmissionDisplayNumber(sub, fallbackIndex) {
    const n = sub?.submissionNumber ?? sub?.submission_number ?? sub?.number ?? null;
    if (n != null && String(n).trim() !== '' && String(n) !== 'undefined') return n;
    return fallbackIndex != null ? fallbackIndex + 1 : null;
}

function qaSubmissionNotesMarkdown(sub) {
    const notes = sub?.notes;
    const fromObj = notes && typeof notes === 'object' && typeof notes.markdown === 'string' ? notes.markdown : null;
    if (fromObj != null) return fromObj;
    if (typeof notes === 'string') return notes;
    return '';
}

function qaSubmissionSubmittedAtPipe(sub) {
    const raw =
        sub?.submittedAt ?? sub?.submitted_at ?? sub?.createdAt ?? sub?.created_at ?? null;
    return raw ? formatLongDateTimeWithPipe(raw) : null;
}

/**
 * Avatar/meta for submission grouping cards (SID row).
 * @returns {{ name: string, id?: *, initials?: string, color?: string } | null}
 */
export function qaSubmissionSubmittedByPresentation(sub) {
    if (!sub || typeof sub !== 'object') return null;
    const u = sub.submittedBy ?? sub.submitted_by ?? sub.createdBy ?? sub.created_by ?? null;
    const nameRaw =
        (u && typeof u === 'object' ? u.name : null) ??
        sub.submittedByName ??
        sub.submitted_by_name ??
        null;
    const name = nameRaw != null ? String(nameRaw).trim() : '';
    const id = u && typeof u === 'object' ? u.id ?? u._id : null;
    if (!name && id == null) return null;
    const displayName = name || 'User';
    return {
        name: displayName,
        id,
        initials: getInitials(displayName),
        color: getAvatarColor(String(id ?? displayName)),
    };
}

/**
 * Normalize GET /projects/:projectId/sprints/:sprintId payload: sprint fields, tasks, and submission rows for Submitted column grouping.
 */
export function normalizeProjectSprintDetailPayload(raw) {
    if (raw == null || typeof raw !== 'object') {
        return { sprintFields: {}, tasks: [], submissions: [] };
    }
    const root = raw.data != null && typeof raw.data === 'object' ? raw.data : raw;
    const tasks = Array.isArray(root.tasks)
        ? root.tasks
        : Array.isArray(root.sprintTasks)
            ? root.sprintTasks
            : [];
    const submissions = Array.isArray(root.submissions)
        ? root.submissions
        : Array.isArray(root.qaSubmissions)
            ? root.qaSubmissions
            : Array.isArray(root.qa_submissions)
                ? root.qa_submissions
                : [];
    const sprintFields = { ...root };
    delete sprintFields.tasks;
    delete sprintFields.sprintTasks;
    delete sprintFields.submissions;
    delete sprintFields.qaSubmissions;
    delete sprintFields.qa_submissions;
    return { sprintFields, tasks, submissions };
}

/**
 * Overlay sprint-by-id response onto the flattened sprint list from project detail (milestones → sprints).
 */
export function mergeSprintDetailIntoSprints(baseSprints, detailRaw, selectedSprintId) {
    if (!Array.isArray(baseSprints) || selectedSprintId == null) return baseSprints;
    if (detailRaw == null) return baseSprints;
    const { sprintFields, tasks } = normalizeProjectSprintDetailPayload(detailRaw);
    return baseSprints.map((s) => {
        if (String(s.id) !== String(selectedSprintId)) return s;
        return {
            ...s,
            ...sprintFields,
            tasks,
        };
    });
}

/**
 * Sprint Kanban "Submitted" column: group `submittedTasks` by project QA submission payloads (same {@link QASubmissionGroupCard} as QA tab TODO).
 *
 * @returns {{ groups: Array<Record<string, unknown>>, orphanTasks: Array }}
 */
export function buildSprintSubmittedColumnSubmissionLayout({ sprintId, submittedTasks, qaSubmissions }) {
    const submittedList = Array.isArray(submittedTasks) ? submittedTasks : [];
    const list = Array.isArray(qaSubmissions) ? qaSubmissions : [];

    if (!sprintId) {
        return { groups: [], orphanTasks: submittedList.slice() };
    }

    if (submittedList.length === 0 && list.length === 0) {
        return { groups: [], orphanTasks: [] };
    }

    const groups = [];
    const matchedIds = new Set();

    const boardTaskMatchesRefId = (boardTask, refSid) => {
        const s = String(refSid ?? '').trim();
        if (!s) return false;
        const api = String(getSprintTaskApiId(boardTask) ?? '');
        const row = String(boardTask?.id ?? '');
        const tid = String(boardTask?.taskId ?? '');
        return s === api || s === row || s === tid;
    };

    /**
     * Sprint-detail API may send `tasks: ["<uuid>", …]` (taskId or sprint-task row id).
     * Resolve against the Submitted column list; if missing, surface the raw id as the label.
     */
    const resolveBoardTask = (remote) => {
        let refId = null;
        if (remote == null) return null;
        if (typeof remote === 'string' || typeof remote === 'number') {
            refId = String(remote).trim();
        } else if (typeof remote === 'object') {
            refId = String(getSprintTaskApiId(remote) ?? remote?.taskId ?? remote?.id ?? '').trim();
        }
        if (!refId) return null;

        const fromBoard = submittedList.find((t) => boardTaskMatchesRefId(t, refId));

        if (fromBoard) {
            const k1 = String(getSprintTaskApiId(fromBoard) ?? '');
            const k2 = String(fromBoard?.id ?? '');
            if (k1) matchedIds.add(k1);
            if (k2) matchedIds.add(k2);
            return fromBoard;
        }

        const isObj = typeof remote === 'object' && remote !== null;
        return {
            taskId: refId,
            id: refId,
            title: isObj ? (remote.title ?? remote.name ?? refId) : refId,
            name: isObj ? (remote.name ?? remote.title ?? refId) : refId,
            taskNumber: isObj ? (remote.taskNumber ?? remote.task_number) : undefined,
            type: isObj ? remote.type : undefined,
            status: 'submitted_to_qa',
        };
    };

    for (let idx = 0; idx < list.length; idx++) {
        const sub = list[idx];
        const recordId = extractQaSubmissionRecordId(sub) ?? `sub-${idx}`;
        const n = qaSubmissionDisplayNumber(sub, idx);
        const labelN = n != null ? n : idx + 1;
        const sidLabel = String(labelN).toUpperCase().startsWith('SUB-') ? labelN : `SUB-${labelN}`;
        const submissionTitle = `Submission ${labelN}`;
        const statusLabel =
            sub?.status ?? sub?.qaStatus ?? sub?.submissionStatus ?? '';

        const tasksRaw = Array.isArray(sub?.tasks) ? sub.tasks : [];
        const sprintTasksRaw = tasksRaw
            .map((t) => {
                if (t == null) return null;
                if (typeof t === 'string' || typeof t === 'number') {
                    const raw = String(t).trim();
                    return raw || null;
                }
                if (typeof t === 'object') {
                    const sid = String(t?.sprintId ?? t?.sprint?.id ?? '');
                    if (sid && sid !== String(sprintId)) return null;
                    return t;
                }
                return null;
            })
            .filter((x) => x != null);

        const tasksForCard = sprintTasksRaw.map(resolveBoardTask).filter(Boolean);
        if (tasksForCard.length === 0) continue;

        const attachmentCount =
            sub?.attachmentCount ??
            sub?.attachmentsCount ??
            normalizeSubmissionAttachmentRecords(sub).length;

        groups.push({
            id: String(recordId),
            sidLabel,
            submissionTitle,
            submissionStatus: statusLabel,
            descriptionMarkdown: qaSubmissionNotesMarkdown(sub),
            descriptionMentions: sub?.descriptionMentions,
            attachmentCount,
            submittedAtLabel: qaSubmissionSubmittedAtPipe(sub),
            submittedBy: qaSubmissionSubmittedByPresentation(sub),
            tasks: tasksForCard,
        });
    }

    const orphanTasks = submittedList.filter((t) => {
        const id = String(getSprintTaskApiId(t) ?? t?.id ?? t?.taskId ?? '');
        return id && !matchedIds.has(id);
    });

    return { groups, orphanTasks };
}

/**
 * PMO submission records from GET /projects/:id/qa/submissions list payload.
 */
export function extractPmoSubmissionsFromQaList(allSubmissions) {
    const list = Array.isArray(allSubmissions) ? allSubmissions : [];
    const explicitPmo = list.filter(
        (s) => s.submittedTo === 'pmo' || s.submissionType === 'pmo',
    );
    const extractedPmo = [];
    for (const s of list) {
        if (s?.pmoSubmission?.submissionId) {
            extractedPmo.push({
                id: s.pmoSubmission.submissionId,
                milestoneId: s.milestoneId,
                projectId: s.projectId,
                submittedBy: s.pmoSubmission.submittedBy ?? s.submittedBy,
                submittedTo: 'pmo',
                status: s.status ?? s.pmoSubmission.status,
                notes: s.pmoSubmission.notes ?? s.notes,
                content: s.pmoSubmission.content ?? s.content,
                links: s.pmoSubmission.links || s.links || [],
                attachments: s.pmoSubmission.attachments || s.attachments || [],
                submittedAt: s.pmoSubmission.submittedAt ?? s.submittedAt,
                milestoneName: s.milestoneName,
                submissionType: s.submissionType,
                milestone: s.milestone,
                sprints: s.sprints || [],
                tasks: s.tasks || [],
                buildId: s.buildId ?? s.pmoSubmission.buildId ?? null,
                build: s.build ?? s.pmoSubmission?.build ?? null,
                descriptionMentions: s.descriptionMentions ?? s.pmoSubmission?.descriptionMentions,
            });
        }
    }
    const combined = [...explicitPmo, ...extractedPmo];
    const seen = new Set();
    return combined.filter((sub) => {
        const sid = extractQaSubmissionRecordId(sub);
        if (!sid || seen.has(String(sid))) return false;
        seen.add(String(sid));
        return true;
    });
}

/**
 * QA submission records (submittedTo: 'qa') from GET /projects/:id/qa/submissions list payload.
 */
export function extractQaSubmissionsFromQaList(allSubmissions) {
    const list = Array.isArray(allSubmissions) ? allSubmissions : [];
    const qaList = list.filter(
        (s) => s.submittedTo === 'qa' || (!s.submittedTo && s.submissionType !== 'pmo'),
    );
    const seen = new Set();
    return qaList.filter((sub) => {
        const sid = extractQaSubmissionRecordId(sub);
        if (!sid || seen.has(String(sid))) return false;
        seen.add(String(sid));
        return true;
    });
}

function collectWorkspaceTaskIdentityKeys(task) {
    const keys = new Set();
    const add = (value) => {
        const s = String(value ?? '').trim();
        if (s && s !== 'undefined') keys.add(s);
    };
    add(getSprintTaskApiId(task));
    add(task?.originalTaskId);
    add(task?.id);
    add(task?.rawId);
    add(task?.taskId);
    add(task?.taskNumber);
    return keys;
}

function workspaceTasksShareIdentity(a, b) {
    const keysA = collectWorkspaceTaskIdentityKeys(a);
    const keysB = collectWorkspaceTaskIdentityKeys(b);
    for (const key of keysA) {
        if (keysB.has(key)) return true;
    }
    return false;
}

/**
 * Workspace kanban "Submitted to PMO" column: group tasks by PMO submission payloads.
 *
 * @returns {{ groups: Array<Record<string, unknown>>, orphanTasks: Array }}
 */
export function buildWorkspacePmoSubmissionColumnLayout({ pmoColumnTasks, pmoSubmissions }) {
    const taskList = Array.isArray(pmoColumnTasks) ? pmoColumnTasks : [];
    const list = Array.isArray(pmoSubmissions) ? pmoSubmissions : [];

    if (taskList.length === 0 && list.length === 0) {
        return { groups: [], orphanTasks: [] };
    }

    const pmoIdSet = new Set(
        taskList
            .map((t) => String(t.originalTaskId || t.taskId || t.rawId || t.id || '').trim())
            .filter((id) => id && id !== 'undefined'),
    );

    const groups = [];

    const boardTaskMatchesRefId = (boardTask, refSid) => {
        const s = String(refSid ?? '').trim();
        if (!s) return false;
        const api = String(getSprintTaskApiId(boardTask) ?? boardTask?.originalTaskId ?? '');
        const row = String(boardTask?.id ?? boardTask?.rawId ?? '');
        const tid = String(boardTask?.taskId ?? '');
        return s === api || s === row || s === tid;
    };

    const resolveBoardTask = (remote) => {
        let refId = null;
        if (remote == null) return null;
        if (typeof remote === 'string' || typeof remote === 'number') {
            refId = String(remote).trim();
        } else if (typeof remote === 'object') {
            refId = String(getSprintTaskApiId(remote) ?? remote?.taskId ?? remote?.id ?? '').trim();
        }
        if (!refId) return null;

        const fromBoard = taskList.find((t) => boardTaskMatchesRefId(t, refId));
        if (fromBoard) {
            return fromBoard;
        }

        const isObj = typeof remote === 'object' && remote !== null;
        return {
            taskId: refId,
            id: refId,
            originalTaskId: refId,
            title: isObj ? (remote.title ?? remote.name ?? refId) : refId,
            name: isObj ? (remote.name ?? remote.title ?? refId) : refId,
            taskNumber: isObj ? (remote.taskNumber ?? remote.task_number) : undefined,
            type: isObj ? remote.type : undefined,
            status: 'submitted_to_pmo',
        };
    };

    for (let idx = 0; idx < list.length; idx++) {
        const sub = list[idx];
        const recordId = extractQaSubmissionRecordId(sub) ?? `pmo-sub-${idx}`;
        const n = qaSubmissionDisplayNumber(sub, idx);
        const labelN = n != null ? n : idx + 1;
        const sidLabel = String(labelN).toUpperCase().startsWith('PMO-') ? labelN : `PMO-${labelN}`;
        const submissionTitle = `Submission ${labelN}`;
        const statusLabel = sub?.status ?? sub?.qaStatus ?? sub?.submissionStatus ?? '';

        const tasksRaw = Array.isArray(sub?.tasks) ? sub.tasks : [];
        const tasksForCard = tasksRaw.map(resolveBoardTask).filter(Boolean);
        if (tasksForCard.length === 0) continue;

        const attachmentCount =
            sub?.attachmentCount ??
            sub?.attachmentsCount ??
            normalizeSubmissionAttachmentRecords(sub).length;

        groups.push({
            id: String(recordId),
            sidLabel,
            submissionTitle,
            statusLabel,
            descriptionMarkdown: qaSubmissionNotesMarkdown(sub),
            descriptionMentions: sub?.descriptionMentions,
            attachmentCount,
            submittedAtLabel: qaSubmissionSubmittedAtPipe(sub),
            submittedBy: qaSubmissionSubmittedByPresentation(sub),
            tasks: tasksForCard,
            rawSubmission: sub,
        });
    }

    const orphanTasks = taskList.filter((boardTask) => (
        !groups.some((group) => (group.tasks || []).some((groupTask) => workspaceTasksShareIdentity(boardTask, groupTask)))
    ));

    if (groups.length === 0 && pmoIdSet.size > 0) {
        return { groups: [], orphanTasks: taskList.slice() };
    }

    return { groups, orphanTasks };
}

/**
 * Workspace kanban "Ready for QA" column: group tasks by QA submission payloads.
 *
 * @returns {{ groups: Array<Record<string, unknown>>, orphanTasks: Array }}
 */
export function buildWorkspaceQaSubmissionColumnLayout({ qaColumnTasks, qaSubmissions, allBoardTasks, targetColumn = 'qa_todo' }) {
    const taskList = Array.isArray(qaColumnTasks) ? qaColumnTasks : [];
    const list = Array.isArray(qaSubmissions) ? qaSubmissions : [];
    const allTasks = Array.isArray(allBoardTasks) ? allBoardTasks : taskList;

    if (taskList.length === 0 && list.length === 0) {
        return { groups: [], orphanTasks: [] };
    }

    const qaIdSet = new Set(
        taskList
            .map((t) => String(t.originalTaskId || t.taskId || t.rawId || t.id || '').trim())
            .filter((id) => id && id !== 'undefined'),
    );

    const groups = [];

    const boardTaskMatchesRefId = (boardTask, refSid) => {
        const s = String(refSid ?? '').trim();
        if (!s) return false;
        const api = String(getSprintTaskApiId(boardTask) ?? boardTask?.originalTaskId ?? '');
        const row = String(boardTask?.id ?? boardTask?.rawId ?? '');
        const tid = String(boardTask?.taskId ?? '');
        return s === api || s === row || s === tid;
    };

    const resolveBoardTask = (remote) => {
        if (remote == null) return null;

        const fromBoard = taskList.find((t) => workspaceTasksShareIdentity(t, remote));
        if (fromBoard) {
            return fromBoard;
        }

        let refId = null;
        if (typeof remote === 'string' || typeof remote === 'number') {
            refId = String(remote).trim();
        } else if (typeof remote === 'object') {
            refId = String(getSprintTaskApiId(remote) ?? remote?.taskId ?? remote?.id ?? remote?.originalTaskId ?? '').trim();
        }
        if (refId) {
            const fromBoardRef = taskList.find((t) => boardTaskMatchesRefId(t, refId));
            if (fromBoardRef) return fromBoardRef;
        }

        const existsInOtherBoardCol = allTasks.some(
            (t) => workspaceTasksShareIdentity(t, remote) || (refId && boardTaskMatchesRefId(t, refId)),
        );
        if (existsInOtherBoardCol) {
            return null;
        }

        const remoteStatus = String(remote?.status ?? '').toLowerCase();
        if (targetColumn === 'qa_todo' && (remoteStatus === 'returned' || remoteStatus === 'qa_bugs' || remoteStatus === 'failed_qa')) {
            return null;
        }

        const isObj = typeof remote === 'object' && remote !== null;
        if (isObj && (remote.title || remote.name || remote.taskNumber || remote.taskId || remote.id)) {
            return {
                taskId: refId || remote.id || remote.taskId,
                id: refId || remote.id || remote.taskId,
                originalTaskId: refId || remote.id || remote.taskId,
                title: remote.title ?? remote.name ?? refId ?? 'Task',
                name: remote.name ?? remote.title ?? refId ?? 'Task',
                taskNumber: remote.taskNumber ?? remote.task_number,
                type: remote.type,
                status: remote.status || (targetColumn === 'qa_bugs' ? 'returned' : 'submitted_to_qa'),
            };
        }

        return null;
    };

    for (let idx = 0; idx < list.length; idx++) {
        const sub = list[idx];
        const subStatus = String(sub?.status ?? sub?.qaStatus ?? sub?.submissionStatus ?? '').toLowerCase();
        const hasSubmissionBugs =
            subStatus === 'returned' ||
            subStatus === 'qa_bugs' ||
            subStatus === 'failed_qa' ||
            Boolean(sub?.hasBugs) ||
            Number(sub?.bugsCount ?? 0) > 0 ||
            (Array.isArray(sub?.bugs) && sub.bugs.length > 0);

        if (targetColumn === 'qa_todo' && hasSubmissionBugs) {
            continue;
        }

        const recordId = extractQaSubmissionRecordId(sub) ?? `qa-sub-${idx}`;
        const n = qaSubmissionDisplayNumber(sub, idx);
        const labelN = n != null ? n : idx + 1;
        const sidLabel = String(labelN).toUpperCase().startsWith('SID-') || String(labelN).toUpperCase().startsWith('SUB-') ? labelN : `SID-${labelN}`;
        const submissionTitle = `Submission ${labelN}`;
        const statusLabel = sub?.status ?? sub?.qaStatus ?? sub?.submissionStatus ?? '';

        const tasksRaw = Array.isArray(sub?.tasks) ? sub.tasks : [];
        const tasksForCard = tasksRaw.map(resolveBoardTask).filter(Boolean);
        if (tasksForCard.length === 0) continue;

        if (targetColumn === 'qa_todo' && tasksForCard.every((t) => String(t.status || '').toLowerCase() === 'returned')) {
            continue;
        }

        const attachmentCount =
            sub?.attachmentCount ??
            sub?.attachmentsCount ??
            normalizeSubmissionAttachmentRecords(sub).length;

        groups.push({
            id: String(recordId),
            sidLabel,
            submissionTitle,
            statusLabel,
            descriptionMarkdown: qaSubmissionNotesMarkdown(sub),
            descriptionMentions: sub?.descriptionMentions,
            attachmentCount,
            submittedAtLabel: qaSubmissionSubmittedAtPipe(sub),
            submittedBy: qaSubmissionSubmittedByPresentation(sub),
            tasks: tasksForCard,
            rawSubmission: sub,
        });
    }

    const orphanTasks = taskList.filter((boardTask) => (
        !groups.some((group) => (group.tasks || []).some((groupTask) => workspaceTasksShareIdentity(boardTask, groupTask)))
    ));

    if (groups.length === 0 && qaIdSet.size > 0) {
        return { groups: [], orphanTasks: taskList.slice() };
    }

    return { groups, orphanTasks };
}

export function normalizeSubmissionAttachmentRecords(submission) {
    if (!submission || typeof submission !== 'object') return [];
    const merged = [];
    const pushAll = (items) => {
        if (!Array.isArray(items)) return;
        for (const it of items) merged.push(it);
    };
    pushAll(submission.attachments);
    pushAll(submission.links);
    pushAll(submission.files);

    const notesAtt =
        submission.notes && typeof submission.notes === 'object' && Array.isArray(submission.notes.attachments)
            ? submission.notes.attachments
            : [];
    pushAll(notesAtt);

    const seen = new Set();
    const out = [];
    for (const x of merged) {
        if (!x) continue;
        if (typeof x === 'string') {
            const url = x.trim();
            if (!url) continue;
            const type = isLikelyFileAttachmentUrl(url) ? 'file' : 'link';
            const key = `${type}|${url}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push({ type, url, title: url });
            continue;
        }
        const url = String(x.url ?? x.href ?? '').trim();
        if (!url) continue;
        const rawType = String(x.type ?? '').toLowerCase();
        const inferred = isLikelyFileAttachmentUrl(url) ? 'file' : 'link';
        const type = rawType === 'file' || rawType === 'link' ? rawType : inferred;
        const title = String(x.title ?? x.name ?? x.filename ?? url);
        const key = `${type}|${url}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ type, url, title });
    }
    return out;
}

/**
 * Slices for the task-style Attachments tab (links row + file tiles) on submission detail modals.
 *
 * @returns {{
 *   attachmentCount: number,
 *   attachmentLinks: Array<{ id: string, rawUrl: string, name: string }>,
 *   attachmentFilesPresentation: Array<{ item: { id: string, name: string }, href: string, isImage: boolean, ext: string }>,
 * }}
 */
export function buildSubmissionAttachmentTabSlices(submission) {
    const attachments = normalizeSubmissionAttachmentRecords(submission);
    const links = attachments.filter((x) => x.type === 'link' && x?.url && !isLikelyFileAttachmentUrl(x.url));
    const files = attachments.filter((x) => x?.url && (x.type !== 'link' || isLikelyFileAttachmentUrl(x.url)));

    const attachmentLinks = links.map((l, idx) => ({
        id: `submission-link-${idx}-${l.url}`,
        rawUrl: l.url,
        name: String(l.title || l.url || '').trim() || String(l.url || ''),
    }));

    const attachmentFilesPresentation = files.map((f, idx) => {
        const href = resolveAttachmentUrl(f.url);
        const isImage = isLikelyImageAttachmentUrl(f.url);
        const ext = getAttachmentFileExtension(f.url);
        return {
            item: {
                id: `submission-file-${idx}-${f.url}`,
                name: String(f.title || '').trim() || String(f.url || '').trim() || 'File',
            },
            href,
            isImage,
            ext,
        };
    });

    return {
        attachmentCount: attachments.length,
        attachmentLinks,
        attachmentFilesPresentation,
    };
}

/**
 * Phase attachments from API: may be on `phase.attachments` and/or `phase.description.attachments`.
 * Dedupes by type + url.
 * @returns {Array<{ type: string, url: string, title?: string }>}
 */
export function getPhaseAttachmentList(phase) {
    if (!phase) return [];
    const raw = Array.isArray(phase.attachments) ? phase.attachments : [];
    const fromDesc =
        phase.description && typeof phase.description === 'object' && Array.isArray(phase.description.attachments)
            ? phase.description.attachments
            : [];
    const merged = [...raw, ...fromDesc];
    const seen = new Set();
    const out = [];
    for (const a of merged) {
        if (!a || typeof a !== 'object') continue;
        const url = typeof a.url === 'string' ? a.url.trim() : '';
        if (!url) continue;
        const type = String(a.type || 'file').toLowerCase();
        const key = `${type}|${url}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
            type,
            url,
            title: typeof a.title === 'string' && a.title.trim() ? a.title.trim() : url,
        });
    }
    return out;
}

/**
 * Milestone attachments from API (same shape as phase): `milestone.attachments` and/or `description.attachments`.
 * @returns {Array<{ type: string, url: string, title?: string }>}
 */
export function getMilestoneAttachmentList(milestone) {
    if (!milestone) return [];
    const raw = Array.isArray(milestone.attachments) ? milestone.attachments : [];
    const fromDesc =
        milestone.description && typeof milestone.description === 'object' && Array.isArray(milestone.description.attachments)
            ? milestone.description.attachments
            : [];
    const merged = [...raw, ...fromDesc];
    const seen = new Set();
    const out = [];
    for (const a of merged) {
        if (!a || typeof a !== 'object') continue;
        const url = typeof a.url === 'string' ? a.url.trim() : '';
        if (!url) continue;
        const type = String(a.type || (isLikelyFileAttachmentUrl(url) ? 'file' : 'link')).toLowerCase();
        const key = `${type}|${url}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
            type,
            url,
            title: typeof a.title === 'string' && a.title.trim() ? a.title.trim() : url,
        });
    }
    return out;
}

/**
 * Sprint attachments from API (same shape as phase/milestone): `sprint.attachments` and/or `description.attachments`.
 * @returns {Array<{ type: string, url: string, title?: string }>}
 */
export function getSprintAttachmentList(sprint) {
    if (!sprint) return [];
    const raw = Array.isArray(sprint.attachments) ? sprint.attachments : [];
    const fromDesc =
        sprint.description && typeof sprint.description === 'object' && Array.isArray(sprint.description.attachments)
            ? sprint.description.attachments
            : [];
    const merged = [...raw, ...fromDesc];
    const seen = new Set();
    const out = [];
    for (const a of merged) {
        if (!a || typeof a !== 'object') continue;
        const url = typeof a.url === 'string' ? a.url.trim() : '';
        if (!url) continue;
        const type = String(a.type || 'file').toLowerCase();
        const key = `${type}|${url}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
            type,
            url,
            title: typeof a.title === 'string' && a.title.trim() ? a.title.trim() : url,
        });
    }
    return out;
}

/** Rich-text HTML → API string for POST/PATCH phase `description`. */
export const buildPhaseDescription = (html) => {
    if (!html || typeof html !== 'string') return undefined;
    const trimmed = html.trim();
    return trimmed || undefined;
};

export function findSubmissionForMilestone(project, milestoneId) {
    if (milestoneId == null) return null;
    const subs = project?.submissions ?? [];
    return subs.find((s) => String(s.milestoneId) === String(milestoneId)) ?? null;
}

/** Sprint list for @-mentions in milestone create (from nested project payload). */
export function collectSprintsFromProjectMilestones(project) {
    const ms = project?.milestones ?? [];
    const sprints = [];
    for (const m of ms) {
        for (const sp of m.sprints ?? []) {
            sprints.push({ ...sp, milestoneId: m.id, milestoneName: m.name });
        }
    }
    return sprints;
}

/**
 * PMO submission progress: percent and counts from totalTasks / submittedToPmo.
 * @param {{ totalTasks?: number, submittedToPmo?: number }} progress
 * @returns {{ percent: number, totalTasks: number, submittedToPmo: number }}
 */
export function getPMOSubmissionProgress(progress) {
    const totalTasks = Number(progress?.totalTasks) || 0;
    const submittedToPmo = Number(progress?.submittedToPmo) || 0;
    const percent = totalTasks > 0 ? Math.round((submittedToPmo / totalTasks) * 100) : 0;
    return { percent, totalTasks, submittedToPmo };
}

/**
 * Build hierarchy from bugs API: Milestone → Sprint → Task → Bugs.
 * Each bug has nested milestone, sprint, task; task.bugs contains all bugs for that task.
 * Returns [{ milestone, sprints: [{ sprint, tasks: [{ task, bugs }] }] }].
 * Dedupes by bug id and uses task.bugs from first occurrence for each task.
 */
export function buildBugsHierarchy(bugs) {
    if (!Array.isArray(bugs) || bugs.length === 0) return [];
    const seenBugIds = new Set();
    const milestoneMap = new Map();
    for (const bug of bugs) {
        const bugId = bug?.id;
        if (!bugId) continue;
        if (seenBugIds.has(bugId)) continue;
        seenBugIds.add(bugId);
        const milestone = bug?.milestone;
        const sprint = bug?.sprint;
        const task = bug?.task;
        const milestoneId = milestone?.id;
        const sprintId = sprint?.id;
        const taskId = task?.id ?? bug?.taskId;
        if (!milestoneId || !sprintId || !taskId) continue;
        if (!milestoneMap.has(milestoneId)) {
            milestoneMap.set(milestoneId, { milestone, sprintMap: new Map() });
        }
        const mRow = milestoneMap.get(milestoneId);
        if (!mRow.sprintMap.has(sprintId)) {
            mRow.sprintMap.set(sprintId, { sprint, taskMap: new Map() });
        }
        const sRow = mRow.sprintMap.get(sprintId);
        if (!sRow.taskMap.has(taskId)) {
            sRow.taskMap.set(taskId, { task: { ...task, bugs: [] }, bugs: [] });
        }
        const tRow = sRow.taskMap.get(taskId);
        tRow.bugs.push(bug);
        tRow.task.bugs = tRow.bugs;
    }
    return Array.from(milestoneMap.values()).map((m) => ({
        milestone: m.milestone,
        sprints: Array.from(m.sprintMap.values()).map((s) => ({
            sprint: s.sprint,
            tasks: Array.from(s.taskMap.values()),
        })),
    }));
}

/**
 * Build sprint columns from bugs API (GET /projects/:id/bugs).
 * Each bug has nested milestone, sprint, task. Groups by sprint then by task; each task gets a bugs array.
 * Returns same shape as QA flatSprints: [{ id, name, startDate, endDate, status, description, milestone, tasks }]
 * with each task having { ...task, bugs: [...] }.
 */
export function buildFlatSprintsFromBugs(bugs) {
    if (!Array.isArray(bugs) || bugs.length === 0) return [];
    const seenBugIds = new Set();
    const sprintMap = new Map();
    for (const bug of bugs) {
        const bugId = bug?.id;
        if (!bugId) continue;
        if (seenBugIds.has(bugId)) continue;
        seenBugIds.add(bugId);
        const sprint = bug?.sprint;
        const task = bug?.task;
        const taskId = task?.id ?? bug?.taskId;
        const sprintId = sprint?.id ?? 'no-sprint';
        if (!sprintId || !taskId) continue;
        const sid = String(sprintId);
        if (!sprintMap.has(sid)) {
            const milestoneObj = bug?.milestone ?? sprint?.milestone ?? null;
            sprintMap.set(sid, {
                id: sid,
                name: sid === 'no-sprint' ? 'No Sprint' : (sprint?.name ?? 'Sprint'),
                startDate: sprint?.startDate,
                endDate: sprint?.endDate,
                status: sprint?.status,
                description: sprint?.description,
                milestoneId: sprint?.milestoneId ?? sprint?.milestone?.id ?? milestoneObj?.id ?? null,
                milestone: milestoneObj,
                phaseId: sprint?.phaseId ?? sprint?.phase_id ?? null,
                phase: sprint?.phase ?? null,
                tasksMap: new Map(),
            });
        }
        const row = sprintMap.get(sid);
        const taskKey = String(getSprintTaskApiId(task) ?? task?.id ?? bug?.taskId ?? '').trim();
        if (!taskKey) continue;
        if (!row.tasksMap.has(taskKey)) {
            const apiTid = getSprintTaskApiId(task) ?? task?.taskId ?? task?.id ?? taskKey;
            row.tasksMap.set(taskKey, {
                id: task?.id ?? taskKey,
                taskId: apiTid,
                taskNumber: task?.taskNumber ?? task?.task_number ?? null,
                title: task?.title ?? task?.name ?? '—',
                name: task?.title ?? task?.name ?? '—',
                description: task?.description,
                priority: task?.priority,
                status: task?.status,
                type: task?.type,
                bugs: [],
            });
        }
        row.tasksMap.get(taskKey).bugs.push(bug);
    }
    return Array.from(sprintMap.values()).map((s) => ({
        id: s.id,
        name: s.name,
        startDate: s.startDate,
        endDate: s.endDate,
        status: s.status,
        description: s.description,
        milestoneId: s.milestoneId,
        milestone: s.milestone,
        tasks: Array.from(s.tasksMap.values()),
    }));
}

function dedupeBugsById(bugList) {
    const list = Array.isArray(bugList) ? bugList : [];
    const seen = new Set();
    const out = [];
    for (const b of list) {
        const bid = b?.id ?? b?.bugId;
        const key = bid != null ? String(bid) : JSON.stringify(b);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(b);
    }
    return out;
}

/**
 * QA tab: merge sprints from QA submissions with sprints/tasks implied by GET /projects/:id/bugs
 * so tasks with bugs appear (Bug reported column) even when those tasks are not yet on a submission payload.
 * Submission rows win on overlapping task fields; bugs arrays are merged and deduped by bug id.
 */
export function mergeQaSubmissionSprintsWithBugSprints(submissionSprints, bugSprints) {
    const sprintMap = new Map();

    const upsertSprintTasks = (sp) => {
        if (sp?.id == null) return;
        const sid = String(sp.id);
        if (!sprintMap.has(sid)) {
            sprintMap.set(sid, {
                id: sid,
                name: sp.name ?? 'Sprint',
                sprintNumber: sp.sprintNumber ?? sp.sprint_number ?? null,
                startDate: sp.startDate,
                endDate: sp.endDate,
                status: sp.status,
                description: sp.description,
                milestoneId: sp.milestoneId ?? sp.milestone?.id ?? null,
                milestone: sp.milestone ?? null,
                phaseId: sp.phaseId ?? sp.phase?.id ?? null,
                phase: sp.phase ?? null,
                tasksMap: new Map(),
            });
        }
        const target = sprintMap.get(sid);
        if (!target.name && sp.name) target.name = sp.name;
        if (target.sprintNumber == null && (sp.sprintNumber ?? sp.sprint_number) != null) {
            target.sprintNumber = sp.sprintNumber ?? sp.sprint_number;
        }
        if (!target.startDate && sp.startDate) target.startDate = sp.startDate;
        if (!target.endDate && sp.endDate) target.endDate = sp.endDate;
        if (target.status == null && sp.status != null) target.status = sp.status;
        if (!target.description && sp.description) target.description = sp.description;
        if (target.milestoneId == null && (sp.milestoneId ?? sp.milestone?.id) != null) {
            target.milestoneId = sp.milestoneId ?? sp.milestone?.id;
        }
        if (!target.milestone && sp.milestone) target.milestone = sp.milestone;
        if (target.phaseId == null && (sp.phaseId ?? sp.phase?.id) != null) {
            target.phaseId = sp.phaseId ?? sp.phase?.id;
        }
        if (!target.phase && sp.phase) target.phase = sp.phase;

        const tasks = Array.isArray(sp.tasks) ? sp.tasks : [];
        for (const t of tasks) {
            const k = String(getSprintTaskApiId(t) ?? t?.taskId ?? t?.id ?? '').trim();
            if (!k) continue;

            if (!target.tasksMap.has(k)) {
                target.tasksMap.set(k, { ...t });
                continue;
            }
            const prev = target.tasksMap.get(k);
            const mergedBugs = dedupeBugsById([
                ...(Array.isArray(prev.bugs) ? prev.bugs : []),
                ...(Array.isArray(t.bugs) ? t.bugs : []),
            ]);
            target.tasksMap.set(k, {
                ...t,
                ...prev,
                bugs: mergedBugs,
            });
        }
    };

    (Array.isArray(submissionSprints) ? submissionSprints : []).forEach(upsertSprintTasks);
    (Array.isArray(bugSprints) ? bugSprints : []).forEach(upsertSprintTasks);

    return Array.from(sprintMap.values()).map((row) => ({
        id: row.id,
        name: row.name,
        sprintNumber: row.sprintNumber,
        startDate: row.startDate,
        endDate: row.endDate,
        status: row.status,
        description: row.description,
        milestoneId: row.milestoneId,
        milestone: row.milestone,
        phaseId: row.phaseId,
        phase: row.phase,
        tasks: Array.from(row.tasksMap.values()),
    }));
}

/**
 * Build flat table rows from flatSprints for bugs table view.
 * One row per bug. Returns rows with rowSpan info for Milestone, Sprint, Task (merge cells).
 * Also returns KPI counts for analytics cards.
 */
export function buildBugsTableRows(flatSprints) {
    if (!Array.isArray(flatSprints) || flatSprints.length === 0) {
        return { rows: [], kpi: { total: 0, highSeverity: 0, open: 0, withMilestone: 0 } };
    }
    const rows = [];
    const milestoneGroups = new Map();
    for (const sprint of flatSprints) {
        const m = sprint.milestone;
        const milestoneKey = m?.id ?? m?.name ?? 'none';
        if (!milestoneGroups.has(milestoneKey)) {
            milestoneGroups.set(milestoneKey, { milestone: m, sprints: [] });
        }
        milestoneGroups.get(milestoneKey).sprints.push(sprint);
    }
    const milestoneOrder = Array.from(milestoneGroups.values());
    for (const { milestone, sprints } of milestoneOrder) {
        const milestoneName = milestone?.name ?? '—';
        for (const sprint of sprints) {
            const sprintName = sprint?.name ?? 'Sprint';
            const tasks = Array.isArray(sprint.tasks) ? sprint.tasks : [];
            for (const task of tasks) {
                const taskName = task?.title ?? task?.name ?? '—';
                const bugList = Array.isArray(task.bugs) ? task.bugs : [];
                for (let i = 0; i < bugList.length; i++) {
                    rows.push({
                        key: `bug-${task.id ?? task.taskId}-${bugList[i]?.id}-${i}`,
                        milestoneName,
                        sprintName,
                        taskName,
                        bug: bugList[i],
                        bugIndexInTask: i + 1,
                        sprint,
                        task,
                        milestoneKey: milestone?.id ?? milestone?.name ?? 'none',
                        sprintId: sprint.id,
                        taskId: task.id ?? task.taskId,
                        milestoneRowSpan: 1,
                        sprintRowSpan: 1,
                        taskRowSpan: 1,
                    });
                }
            }
        }
    }
    let milestoneRowSpan = 0;
    let sprintRowSpan = 0;
    let taskRowSpan = 0;
    let lastMilestoneKey = null;
    let lastSprintId = null;
    let lastTaskId = null;
    for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        if (r.milestoneKey !== lastMilestoneKey) {
            if (lastMilestoneKey != null) {
                for (let j = i - milestoneRowSpan; j < i; j++) rows[j].milestoneRowSpan = j === i - milestoneRowSpan ? milestoneRowSpan : 0;
            }
            lastMilestoneKey = r.milestoneKey;
            milestoneRowSpan = 0;
            let count = 0;
            for (let k = i; k < rows.length && rows[k].milestoneKey === lastMilestoneKey; k++) count++;
            milestoneRowSpan = count;
        }
        if (r.sprintId !== lastSprintId) {
            if (lastSprintId != null) {
                for (let j = i - sprintRowSpan; j < i; j++) rows[j].sprintRowSpan = j === i - sprintRowSpan ? sprintRowSpan : 0;
            }
            lastSprintId = r.sprintId;
            sprintRowSpan = 0;
            let count = 0;
            for (let k = i; k < rows.length && rows[k].sprintId === lastSprintId; k++) count++;
            sprintRowSpan = count;
        }
        if (r.taskId !== lastTaskId) {
            if (lastTaskId != null) {
                for (let j = i - taskRowSpan; j < i; j++) rows[j].taskRowSpan = j === i - taskRowSpan ? taskRowSpan : 0;
            }
            lastTaskId = r.taskId;
            taskRowSpan = 0;
            let count = 0;
            for (let k = i; k < rows.length && rows[k].taskId === lastTaskId; k++) count++;
            taskRowSpan = count;
        }
    }
    if (rows.length > 0) {
        for (let j = rows.length - milestoneRowSpan; j < rows.length; j++) {
            rows[j].milestoneRowSpan = j === rows.length - milestoneRowSpan ? milestoneRowSpan : 0;
        }
        for (let j = rows.length - sprintRowSpan; j < rows.length; j++) {
            rows[j].sprintRowSpan = j === rows.length - sprintRowSpan ? sprintRowSpan : 0;
        }
        for (let j = rows.length - taskRowSpan; j < rows.length; j++) {
            rows[j].taskRowSpan = j === rows.length - taskRowSpan ? taskRowSpan : 0;
        }
    }
    const severityLower = (s) => (s && String(s).toLowerCase()) || '';
    const statusLower = (s) => (s && String(s).toLowerCase()) || '';
    let highSeverity = 0;
    let open = 0;
    for (const r of rows) {
        const b = r.bug;
        if (severityLower(b?.severity) === 'high') highSeverity++;
        const st = statusLower(b?.status);
        if (st === 'open' || st === 'new' || st === 'to_do' || !st) open++;
    }
    const kpi = {
        total: rows.length,
        highSeverity,
        open,
        withMilestone: milestoneOrder.length,
    };
    return { rows, kpi };
}

/** Latest milestone due date (dayjs) in the same scope (e.g. phase) — next milestone must be strictly after this day. */
export const getLastMilestoneDueDate = (milestones) => {
    if (!milestones?.length) return null;
    let latest = null;
    milestones.forEach((m) => {
        const d = m.dueDate ? dayjs(m.dueDate) : null;
        if (d && (!latest || d.isAfter(latest))) latest = d;
    });
    return latest;
};

/**
 * Edit: among milestones in the same phase (excluding the one being edited), find neighbor due dates
 * around `referenceDay` to enforce strict ordering (after previous, before next).
 */
export function getMilestoneDueDateEditBounds(milestonesInPhase, editingMilestoneId, referenceDay) {
    const cur = referenceDay ? dayjs(referenceDay).startOf('day') : null;
    if (!cur || !editingMilestoneId) return { minExclusive: null, maxExclusive: null };
    const others = milestonesInPhase.filter(
        (m) => String(m.id) !== String(editingMilestoneId) && m?.dueDate,
    );
    let minExclusive = null;
    let maxExclusive = null;
    for (const m of others) {
        const d = dayjs(m.dueDate).startOf('day');
        if (d.isBefore(cur)) {
            if (!minExclusive || d.isAfter(minExclusive)) minExclusive = d;
        }
        if (d.isAfter(cur)) {
            if (!maxExclusive || d.isBefore(maxExclusive)) maxExclusive = d;
        }
    }
    return { minExclusive, maxExclusive };
}

/** Create: due date must be strictly after latest in phase and not before today. */
export const createMilestoneDueDateValidator = (lastDue, isRelaxing) => (_, value) => {
    if (!value) return Promise.resolve();
    const d = dayjs(value).startOf('day');
    const today = dayjs().startOf('day');
    if (d.isBefore(today)) {
        return Promise.reject(new Error('Due date cannot be in the past'));
    }
    if (!isRelaxing && lastDue && !d.isAfter(lastDue.startOf('day'))) {
        return Promise.reject(
            new Error(`Due date must be after ${lastDue.format('MMM D, YYYY')}`),
        );
    }
    return Promise.resolve();
};

/** Create: disable calendar days before today and on/before the latest milestone due date in the phase. */
export const createMilestoneDisabledDate = (lastDue, isRelaxing) => (current) => {
    if (!current) return false;
    const day = current.startOf('day');
    if (day.isBefore(dayjs().startOf('day'))) return true;
    if (!isRelaxing && lastDue && !day.isAfter(lastDue.startOf('day'))) return true;
    return false;
};

/** Edit: disable days outside (minExclusive, maxExclusive) vs today. */
export const createMilestoneEditDisabledDate = (bounds, isRelaxing) => (current) => {
    if (!current) return false;
    const day = current.startOf('day');
    if (day.isBefore(dayjs().startOf('day'))) return true;
    if (!isRelaxing) {
        const { minExclusive, maxExclusive } = bounds || {};
        if (minExclusive && !day.isAfter(minExclusive)) return true;
        if (maxExclusive && !day.isBefore(maxExclusive)) return true;
    }
    return false;
};

export const createMilestoneEditDueDateValidator = (bounds, isRelaxing) => (_, value) => {
    if (!value) return Promise.resolve();
    const d = dayjs(value).startOf('day');
    const today = dayjs().startOf('day');
    if (d.isBefore(today)) {
        return Promise.reject(new Error('Due date cannot be in the past'));
    }
    if (!isRelaxing) {
        const { minExclusive, maxExclusive } = bounds || {};
        if (minExclusive && !d.isAfter(minExclusive)) {
            return Promise.reject(new Error(`Due date must be after ${minExclusive.format('MMM D, YYYY')}`));
        }
        if (maxExclusive && !d.isBefore(maxExclusive)) {
            return Promise.reject(new Error(`Due date must be before ${maxExclusive.format('MMM D, YYYY')}`));
        }
    }
    return Promise.resolve();
};

/** Build milestone description payload from rich HTML (summary + goals from list items). */
export const buildMilestoneDescription = (html) => {
    if (!html || typeof html !== 'string') return undefined;
    const trimmed = html.trim();
    if (!trimmed) return undefined;
    const goals = [];
    if (typeof document !== 'undefined') {
        const div = document.createElement('div');
        div.innerHTML = trimmed;
        div.querySelectorAll('ul li, ol li').forEach((li) => {
            const t = (li.textContent || '').trim();
            if (t) goals.push(t);
        });
    }
    return { summary: trimmed, goals };
};

function urlsEqualForMilestoneAttachment(href, itemUrl) {
    const h = String(href || '').trim();
    const u = String(itemUrl || '').trim();
    if (!h || h.startsWith('#') || !u) return false;
    return normalizePhaseLinkUrl(h) === normalizePhaseLinkUrl(u);
}

function milestoneFileAnchorMatchesItem(anchor, item) {
    const href = anchor.getAttribute('href') || '';
    if (!href.startsWith('#attachment')) return false;
    const fullname = (anchor.getAttribute('data-attachment-fullname') || '').trim();
    const itemTitle = String(item.title || '').trim();
    const itemUrl = String(item.url || '').trim();
    const base = itemUrl.split(/[\\/]/).pop() || '';
    let decodedBase = base;
    try {
        decodedBase = decodeURIComponent(base);
    } catch {
        /* keep base */
    }
    if (fullname && itemTitle && fullname === itemTitle) return true;
    if (fullname && base && (fullname === base || fullname === decodedBase)) return true;
    if (fullname && base && (fullname.endsWith(base) || base.endsWith(fullname))) return true;
    return false;
}

/**
 * Remove one attachment anchor from milestone summary HTML (first match).
 */
export function stripMilestoneAttachmentAnchorsFromHtml(html, item) {
    if (!html || typeof html !== 'string' || typeof document === 'undefined') return html;
    const div = document.createElement('div');
    div.innerHTML = html;
    const type = String(item.type || 'file').toLowerCase();
    const anchors = [...div.querySelectorAll('a[href]')];
    if (type === 'link') {
        for (const a of anchors) {
            const href = a.getAttribute('href') || '';
            if (urlsEqualForMilestoneAttachment(href, item.url)) {
                a.remove();
                break;
            }
        }
    } else {
        for (const a of anchors) {
            const href = a.getAttribute('href') || '';
            if (href.startsWith('#attachment') && milestoneFileAnchorMatchesItem(a, item)) {
                a.remove();
                break;
            }
        }
    }
    return div.innerHTML;
}

/** Renumber `#attachment-N` anchors after a removal. */
export function reindexMilestoneAttachmentAnchorsInHtml(html) {
    if (!html || typeof html !== 'string' || typeof document === 'undefined') return html;
    const div = document.createElement('div');
    div.innerHTML = html;
    const anchors = [...div.querySelectorAll('a[href^="#attachment"]')];
    anchors.forEach((a, i) => {
        a.setAttribute('href', `#attachment-${i}`);
        if (a.hasAttribute('data-attachment-index')) {
            a.setAttribute('data-attachment-index', String(i));
        }
    });
    return div.innerHTML;
}

/**
 * PATCH body to remove one attachment from a milestone (top-level `attachments`, nested `description.attachments`, and anchors in `description.summary`).
 * @param {{ attachments?: unknown[], description?: object }} milestone
 * @param {{ type?: string, url: string, title?: string }} item
 */
export function buildRemoveMilestoneAttachmentPatchPayload(milestone, item) {
    if (!milestone || !item) return null;
    const itemUrl = String(item.url || '').trim();
    if (!itemUrl) return null;
    const itemType = String(item.type || 'file').toLowerCase();

    const matchesRecord = (a) => {
        if (!a || typeof a !== 'object') return false;
        return String(a.type || 'file').toLowerCase() === itemType && String(a.url || '').trim() === itemUrl;
    };

    let summaryHtml = getMilestoneDescriptionSummaryHtml(milestone);
    if (summaryHtml && typeof document !== 'undefined') {
        summaryHtml = stripMilestoneAttachmentAnchorsFromHtml(summaryHtml, item);
        summaryHtml = reindexMilestoneAttachmentAnchorsInHtml(summaryHtml);
    }

    const built = buildMilestoneDescription(summaryHtml || '');
    const desc = milestone.description;
    const goalsFallback = Array.isArray(desc?.goals) ? desc.goals : [];
    const descriptionPayload = built ?? { summary: summaryHtml || '', goals: goalsFallback };

    if (typeof desc === 'object' && desc != null && Array.isArray(desc.attachments)) {
        descriptionPayload.attachments = desc.attachments.filter((a) => !matchesRecord(a));
    }

    const body = { description: descriptionPayload };

    if (Array.isArray(milestone.attachments)) {
        body.attachments = milestone.attachments.filter((a) => !matchesRecord(a));
    }

    return body;
}

/** Build sprint description payload from rich HTML. */
export const buildSprintDescription = (html) => {
    if (!html || typeof html !== 'string') return undefined;
    const trimmed = html.trim();
    return trimmed ? { content: trimmed } : undefined;
};

/** Build task description payload from rich HTML (markdown field). */
export const buildTaskDescription = (html) => {
    if (!html || typeof html !== 'string') return undefined;
    const trimmed = html.trim();
    return trimmed ? { markdown: trimmed } : undefined;
};
